import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { Observable, from, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { MyNotificationsViewModel, NotificationProxy } from '@/app/proxy/proxies';

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    private transitionDuration = 200; // milliseconds
    private navigationTimeouts: number[] = [];
    public newNotifications$ = new BehaviorSubject<MyNotificationsViewModel[]>([]);

    constructor(
        private router: Router,
        private notificationProxy: NotificationProxy
    ) {}

    /**
     * Navigate to a route with a smooth transition
     * @param commands The route commands to navigate to
     * @param extras Navigation extras (optional)
     * @returns Observable that resolves when navigation is complete
     */
    public navigateWithTransition(commands: any[], extras?: NavigationExtras): Observable<boolean> {
        // Add the navigating class to the body to fade out the current page
        this.startTransition();

        // Return an observable that completes when navigation is done
        return from(
            new Promise<boolean>((resolve) => {
                // Store timeout ID for potential cleanup
                const timeoutId = window.setTimeout(() => {
                    this.router
                        .navigate(commands, extras)
                        .then((success) => resolve(success))
                        .catch((error) => {
                            console.error('Navigation error:', error);
                            this.endTransition();
                            resolve(false);
                        });
                }, this.transitionDuration);

                // Store the timeout ID for cleanup
                this.navigationTimeouts.push(timeoutId);
            })
        ).pipe(
            finalize(() => {
                // Clean up timeouts when observable completes or errors
                this.clearTimeouts();
            }),
            catchError((error) => {
                this.endTransition();
                return throwError(() => error);
            })
        );
    }

    /**
     * Navigate by URL with a smooth transition
     * @param url The URL to navigate to
     * @param extras Navigation extras (optional)
     * @returns Observable that resolves when navigation is complete
     */
    public navigateByUrlWithTransition(url: string, extras?: NavigationExtras): Observable<boolean> {
        // Add the navigating class to the body to fade out the current page
        this.startTransition();

        // Return an observable that completes when navigation is done
        return from(
            new Promise<boolean>((resolve) => {
                // Store timeout ID for potential cleanup
                const timeoutId = window.setTimeout(() => {
                    this.router
                        .navigateByUrl(url, extras)
                        .then((success) => resolve(success))
                        .catch((error) => {
                            console.error('Navigation error:', error);
                            this.endTransition();
                            resolve(false);
                        });
                }, this.transitionDuration);

                // Store the timeout ID for cleanup
                this.navigationTimeouts.push(timeoutId);
            })
        ).pipe(
            finalize(() => {
                // Clean up timeouts when observable completes or errors
                this.clearTimeouts();
            }),
            catchError((error) => {
                this.endTransition();
                return throwError(() => error);
            })
        );
    }

    /**
     * Start the transition effect
     */
    private startTransition(): void {
        requestAnimationFrame(() => {
            document.body.classList.add('page-navigating');
        });
    }

    /**
     * End the transition effect
     */
    private endTransition(): void {
        requestAnimationFrame(() => {
            document.body.classList.remove('page-navigating');
        });
    }

    /**
     * Clear all navigation timeouts
     */
    private clearTimeouts(): void {
        this.navigationTimeouts.forEach((id) => window.clearTimeout(id));
        this.navigationTimeouts = [];
    }

    /**
     * Fetch notifications
     */
    fetchNotifications(): Observable<MyNotificationsViewModel[]> {
        return this.notificationProxy.notification_GetMyNotifications().pipe(
            map((response) => response.result || []),
            catchError((error) => {
                console.error('Error fetching notifications:', error);
                return of([]);
            })
        );
    }

    /**
     * Refresh notifications
     */
    refreshNotifications(): Observable<MyNotificationsViewModel[]> {
        return this.fetchNotifications();
    }

    /**
     * Mark a notification as read
     */
    markAsRead(notificationId: string): Observable<any> {
        return this.notificationProxy.notification_MarkSeen(notificationId).pipe(
            catchError((error) => {
                console.error('Error marking notification as read:', error);
                return of(null);
            })
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<any> {
        return this.notificationProxy.notification_MarkAllSeen().pipe(
            catchError((error) => {
                console.error('Error marking all notifications as read:', error);
                return of(null);
            })
        );
    }

    /**
     * Update notification status locally and refresh the observable
     */
    updateNotificationStatus(notificationId: string, isSeen: boolean): void {
        const currentNotifications = this.newNotifications$.value;
        const updatedNotifications = currentNotifications.map((notification) => {
            if (notification.id === notificationId) {
                const updatedNotification = new MyNotificationsViewModel(notification);
                updatedNotification.isSeen = isSeen;
                return updatedNotification;
            }
            return notification;
        });
        this.newNotifications$.next(updatedNotifications);
    }

    /**
     * Add a real-time notification (prepend + dedupe + emit)
     */
    addRealtimeNotification(notification: MyNotificationsViewModel): void {
        const currentNotifications = this.newNotifications$.value;
        // Dedupe by id - if it already exists, don't add it again
        const exists = currentNotifications.some((n) => n.id === notification.id);
        if (!exists) {
            // Prepend the new notification
            const updatedNotifications = [notification, ...currentNotifications];
            this.newNotifications$.next(updatedNotifications);
        }
    }

    /**
     * Mark all notifications as read locally and refresh the observable
     */
    markAllAsReadLocally(): void {
        const currentNotifications = this.newNotifications$.value;
        const updatedNotifications = currentNotifications.map((notification) => {
            const updatedNotification = new MyNotificationsViewModel(notification);
            updatedNotification.isSeen = true;
            return updatedNotification;
        });
        this.newNotifications$.next(updatedNotifications);
    }
}
